/**
 * StripeCheckoutModal
 * Renders an inline Stripe CardElement modal for provider billing.
 * Wraps itself in <Elements> so it can be dropped anywhere without
 * a parent Elements provider.
 */
import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { X, Lock, CreditCard, CheckCircle } from 'lucide-react';

// Initialise Stripe once at module level
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_STYLE = {
  style: {
    base: {
      fontSize: '15px',
      color: '#1c1917',
      fontFamily: 'Manrope, sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': { color: '#A8B4B8' },
    },
    invalid: { color: '#dc2626', iconColor: '#dc2626' },
  },
};

// Inner form — must be rendered inside <Elements>
function CardForm({ type, label, amount, providerToken, onSuccess, onCancel }) {
  const stripe     = useStripe();
  const elements   = useElements();
  const API        = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const [clientSecret,    setClientSecret]    = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [initLoading,     setInitLoading]     = useState(true);
  const [cardReady,       setCardReady]       = useState(false);
  const [submitting,      setSubmitting]      = useState(false);
  const [error,           setError]           = useState('');

  // Create PaymentIntent as soon as the modal mounts
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(
          `${API}/provider/billing/create-payment-intent`,
          { type },
          { headers: { Authorization: `Bearer ${providerToken}` } }
        );
        setClientSecret(res.data.client_secret);
        setPaymentIntentId(res.data.payment_intent_id);
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not initialise payment. Please try again.');
      } finally {
        setInitLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setSubmitting(true);
    setError('');

    const cardElement = elements.getElement(CardElement);
    const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (stripeErr) {
      setError(stripeErr.message || 'Card declined. Please check your details.');
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        await axios.post(
          `${API}/provider/billing/activate-after-payment`,
          { payment_intent_id: paymentIntentId },
          { headers: { Authorization: `Bearer ${providerToken}` } }
        );
        onSuccess(type);
      } catch {
        // Payment went through on Stripe — tell provider it will activate
        setError('Payment received. Your status will update within 60 seconds. Contact partners@meridiantracker.com if it does not.');
        setSubmitting(false);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      data-testid="stripe-card-modal-overlay"
    >
      <div
        className="bg-white w-full max-w-md shadow-2xl"
        data-testid="stripe-card-modal"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 py-5 border-b border-stone-200"
          style={{ backgroundColor: '#0D3D4A' }}
        >
          <div className="flex-1">
            <p className="text-xs text-white/60 uppercase tracking-widest font-semibold mb-1">
              Meridian Billing
            </p>
            <p className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {label}
            </p>
          </div>
          <div className="text-right mr-5">
            <p className="text-white/60 text-xs">Total due</p>
            <p className="text-white text-xl font-bold">
              ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={submitting}
            className="text-white/50 hover:text-white transition-colors mt-0.5"
            data-testid="stripe-modal-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {initLoading ? (
            <div className="flex items-center justify-center py-10 gap-3 text-stone-500 text-sm">
              <div className="w-5 h-5 border-2 border-[#0D3D4A] border-t-transparent rounded-full animate-spin" />
              Initialising secure payment…
            </div>
          ) : error && !clientSecret ? (
            <div className="py-6 text-center">
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={onCancel}
                className="text-sm underline text-stone-500 hover:text-stone-700"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
                Card Details
              </label>

              {/* Stripe CardElement container */}
              <div
                className="border border-stone-300 px-4 py-3.5 transition-colors focus-within:border-[#0D3D4A]"
                data-testid="stripe-card-element-container"
              >
                <CardElement
                  options={CARD_STYLE}
                  onReady={() => setCardReady(true)}
                />
              </div>

              {error && (
                <p className="mt-3 text-sm text-red-600" data-testid="stripe-card-error">
                  {error}
                </p>
              )}

              <div className="flex items-center gap-1.5 mt-4 text-xs text-stone-400">
                <Lock size={11} />
                Payments secured and processed by Stripe. We never store card data.
              </div>

              <button
                type="submit"
                disabled={!stripe || !cardReady || submitting || !clientSecret}
                className="mt-5 w-full bg-[#0D3D4A] text-white py-3.5 text-sm font-semibold
                           hover:bg-[#0E4557] transition-colors disabled:opacity-50
                           flex items-center justify-center gap-2"
                data-testid="stripe-submit-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <CreditCard size={15} />
                    Pay ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </>
                )}
              </button>

              {process.env.REACT_APP_STRIPE_MODE === 'test' && (
                <p className="mt-3 text-center text-xs text-stone-400">
                  Test card: 4242 4242 4242 4242 · Any future date · Any CVC
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * StripeCheckoutModal — public export
 * Wraps CardForm in its own <Elements> provider so the parent
 * component does not need an Elements ancestor.
 *
 * Props:
 *   type           — 'featured' | 'leads'
 *   label          — display title shown in modal header
 *   amount         — numeric dollar amount (not cents)
 *   providerToken  — JWT bearer token for API calls
 *   onSuccess(type)— called after successful payment + activation
 *   onCancel()     — called when the user dismisses the modal
 */
export function StripeCheckoutModal({ type, label, amount, providerToken, onSuccess, onCancel }) {
  return (
    <Elements stripe={stripePromise}>
      <CardForm
        type={type}
        label={label}
        amount={amount}
        providerToken={providerToken}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
}
